import { useMemo, useState } from "react";
import { MaintenancePlan } from "@/hooks/use-maintenance-plans";
import { WorkOrder } from "@/hooks/use-work-orders";
import { DataTable } from "@/components/admin/data-table";
import { ColumnDef } from "@tanstack/react-table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { 
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { VehicleGroupHeader } from "./VehicleGroupHeader";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Edit, Plus, CheckCircle, Trash2, MoreHorizontal } from "lucide-react";

interface GroupedMaintenancePlansTableProps {
  plans: MaintenancePlan[];
  workOrders: WorkOrder[];
  onEditPlan: (plan: MaintenancePlan) => void;
  onDeletePlan: (planId: string) => void;
  onCreateWorkOrder: (plan: MaintenancePlan) => void;
  onReactivatePlan: (planId: string) => void;
  onViewOpenOrders: (planId: string) => void;
  canEdit: boolean;
  canCreate: boolean;
  canDelete: boolean;
}

// Translation helper functions
const getMaintenanceTypeLabel = (type: string): string => {
  const typeLabels: Record<string, string> = {
    preventive: "Preventiva",
    corrective: "Corretiva",
    oil_change: "Troca de Óleo",
    tire_rotation: "Rodízio de Pneus",
    inspection: "Inspeção",
    other: "Outros"
  };
  return typeLabels[type] || type;
};

const getMaintenanceStatusLabel = (status: string): string => {
  const statusLabels: Record<string, string> = {
    pending: "Pendente",
    active: "Ativo",
    inactive: "Inativo",
    completed: "Concluído",
    in_progress: "Em Andamento"
  };
  return statusLabels[status] || status;
};

export function GroupedMaintenancePlansTable({
  plans,
  workOrders,
  onEditPlan,
  onDeletePlan,
  onCreateWorkOrder,
  onReactivatePlan,
  onViewOpenOrders,
  canEdit,
  canCreate,
  canDelete,
}: GroupedMaintenancePlansTableProps) {
  const [openVehicles, setOpenVehicles] = useState<Set<string>>(new Set());

  // Group plans by vehicle_id
  const groupedPlans = useMemo(() => {
    const groups = new Map<string, MaintenancePlan[]>();
    
    plans.forEach(plan => {
      const vehicleId = plan.vehicle_id;
      if (!groups.has(vehicleId)) {
        groups.set(vehicleId, []);
      }
      groups.get(vehicleId)!.push(plan);
    });
    
    return Array.from(groups.entries()).map(([vehicleId, plans]) => ({
      vehicle: plans[0].fleet_vehicles!,
      plans: plans.sort((a, b) => 
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      )
    }));
  }, [plans]);

  // Calculate open work orders by vehicle
  const openOrdersByVehicle = useMemo(() => {
    const map = new Map<string, number>();
    
    workOrders
      .filter(wo => wo.status === 'open' || wo.status === 'in_progress')
      .forEach(wo => {
        const vehicleId = wo.vehicle_id;
        map.set(vehicleId, (map.get(vehicleId) || 0) + 1);
      });
    
    return map;
  }, [workOrders]);

  // Toggle vehicle expansion
  const toggleVehicle = (vehicleId: string) => {
    setOpenVehicles(prev => {
      const next = new Set(prev);
      if (next.has(vehicleId)) {
        next.delete(vehicleId);
      } else {
        next.add(vehicleId);
      }
      return next;
    });
  };

  // Columns for the internal table (without vehicle column)
  const maintenancePlanColumns: ColumnDef<MaintenancePlan>[] = [
    {
      accessorKey: "name",
      header: "Nome do Plano",
      cell: ({ row }) => {
        const name = row.getValue("name") as string;
        return <span>{name.toUpperCase()}</span>;
      },
    },
    {
      accessorKey: "type",
      header: "Tipo",
      cell: ({ row }) => {
        const type = row.getValue("type") as string;
        return <Badge variant="outline">{getMaintenanceTypeLabel(type)}</Badge>;
      },
    },
    {
      accessorKey: "periodicity_days",
      header: "Periodicidade",
      cell: ({ row }) => {
        const days = row.getValue("periodicity_days") as number;
        const km = row.original.periodicity_km;
        return days ? `${days} dias` : km ? `${km} km` : "N/A";
      },
    },
    {
      accessorKey: "next_execution_date",
      header: "Próxima Execução",
      cell: ({ row }) => {
        const plan = row.original;
        
        // Se a periodicidade é em KM e há próxima execução em KM, mostrar KM
        if (plan.periodicity_km && plan.next_execution_km) {
          return (
            <Badge variant="secondary" className="font-mono">
              {plan.next_execution_km.toLocaleString('pt-BR')} km
            </Badge>
          );
        }
        
        // Senão, mostrar a data (se existir)
        const date = row.getValue("next_execution_date") as string;
        if (date) {
          // Parse manual para evitar problema de timezone com datas YYYY-MM-DD
          const [year, month, day] = date.split('-').map(Number);
          const localDate = new Date(year, month - 1, day);
          
          return (
            <Badge variant="outline" className="font-mono">
              {localDate.toLocaleDateString("pt-BR")}
            </Badge>
          );
        }
        
        return <span className="text-muted-foreground">N/A</span>;
      },
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => {
        const status = row.original.displayStatus || row.original.status;
        const approvalStatus = row.original.approval_status;
        
        const variant = status === "active" ? "default" : 
                        status === "in_progress" ? "success" :
                        status === "pending" ? "warning" :
                        "secondary";
        
        return (
          <div className="flex gap-2 items-center flex-wrap">
            <Badge variant={variant}>
              {getMaintenanceStatusLabel(status)}
            </Badge>
            
            {/* Badge de Status de Aprovação */}
            {approvalStatus === 'pending' && (
              <Badge variant="warning" className="bg-yellow-500 hover:bg-yellow-600 text-white">
                ⏳ Aguardando Aprovação
              </Badge>
            )}
            
            {approvalStatus === 'rejected' && (
              <Badge variant="destructive">
                ❌ Rejeitado
              </Badge>
            )}
          </div>
        );
      },
    },
    {
      id: "work_orders_count",
      header: "OSs",
      cell: ({ row }) => {
        const plan = row.original;
        const planWorkOrders = workOrders.filter(wo => wo.plan_id === plan.id);
        const openOrders = planWorkOrders.filter(wo => wo.status === 'open' || wo.status === 'in_progress');
        
        return (
          <div className="flex gap-2 items-center">
            <Badge variant="secondary">
              {planWorkOrders.length}
            </Badge>
            {openOrders.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onViewOpenOrders(plan.id)}
                className="h-6 px-2"
              >
                <Badge variant="destructive">
                  {openOrders.length} {openOrders.length === 1 ? 'Aberta' : 'Abertas'}
                </Badge>
              </Button>
            )}
          </div>
        );
      }
    },
    {
      id: "actions",
      header: "Ações",
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onEditPlan(row.original)} disabled={!canEdit}>
              <Edit className="mr-2 h-4 w-4" />
              Editar Plano
            </DropdownMenuItem>
            
            {row.original.status === 'active' && canCreate && (
              <DropdownMenuItem 
                onClick={() => onCreateWorkOrder(row.original)}
              >
                <Plus className="mr-2 h-4 w-4" />
                Criar Nova OS
              </DropdownMenuItem>
            )}
            
            {row.original.status === 'pending' && canEdit && (
              <DropdownMenuItem 
                onClick={() => onReactivatePlan(row.original.id)}
              >
                <CheckCircle className="mr-2 h-4 w-4 text-green-600" />
                Ativar Plano
              </DropdownMenuItem>
            )}
            
            {(row.original.status === 'inactive' || row.original.status === 'completed') && canEdit && (
              <DropdownMenuItem 
                onClick={() => onReactivatePlan(row.original.id)}
              >
                <CheckCircle className="mr-2 h-4 w-4 text-green-600" />
                Reativar Plano
              </DropdownMenuItem>
            )}
            
            {canDelete && (
              <DropdownMenuItem 
                onClick={() => onDeletePlan(row.original.id)}
                className="text-destructive"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Excluir
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      {groupedPlans.map(({ vehicle, plans }) => (
        <Collapsible
          key={vehicle.id}
          open={openVehicles.has(vehicle.id)}
          onOpenChange={() => toggleVehicle(vehicle.id)}
          className="border rounded-lg"
        >
          <CollapsibleTrigger asChild>
            <div>
              <VehicleGroupHeader
                vehicle={vehicle}
                plansCount={plans.length}
                openOrdersCount={openOrdersByVehicle.get(vehicle.id) || 0}
                isOpen={openVehicles.has(vehicle.id)}
              />
            </div>
          </CollapsibleTrigger>
          
          <CollapsibleContent className="px-4 pb-4">
            <DataTable 
              columns={maintenancePlanColumns} 
              data={plans}
            />
          </CollapsibleContent>
        </Collapsible>
      ))}
    </div>
  );
}
